import { useState } from 'react';
import { useParams } from 'react-router';
import { Checkbox2 } from '../../../../components/forms/checkbox';
import { FieldGuide, Label, TextInputLarge } from '../../../../components/forms/input';
import { Changed, ChildProps, MapLayerEditorContainer } from './map-layer-editor-container';
import {  Dialog, Title as DialogTitle, Actions as DialogActions, Content as DialogContent } from '../../../../components/dialog/dialog';
import * as Button from '../../../../components/button/button';
import { Bounds, MapLayer } from '../../types/map-layer';
import { useQuery } from '../../../../hooks/use-query';
import { usePutMutation } from '../../../../hooks/use-mutations';
import { Icons } from '../../../../components/icons/icons';
import { MapWrapper } from './map';

export const DrawingInfo = (): JSX.Element => {
  const params = useParams();
  const id = params.id as string;
  const [showMap, setMapVisibility] = useState<boolean>(false);
  const [showDraw, setShowDraw] = useState<boolean>(false);
  const [bounds, setBounds] = useState<Bounds>({});
  const mapLayer = useQuery<MapLayer>({ path: `/map-layers/${id}` });
  const putMutation = usePutMutation<MapLayer>({ path: `/map-layers/${id}`, cacheKey: `/map-layers/${id}` });

  const onChange = (bounds: Bounds): void => {
    setBounds(bounds);
  };

  const onSave = (): void => {
    onCancel();
    putMutation.mutate({
      ...mapLayer.data,
      maxX: bounds.east,
      minX: bounds.west,
      maxY: bounds.north,
      minY: bounds.south,
    });
  };

  const onCancel = (): void => {
    setMapVisibility(false);
    setShowDraw(false);
    setBounds({});
  };

  const getBounds = (): Bounds => {
    return {
      east: mapLayer.data?.maxX,
      north: mapLayer.data?.maxY,
      south: mapLayer.data?.minY,
      west: mapLayer.data?.minX,
    };
  };

  return <>
    <MapLayerEditorContainer>
      {(props: ChildProps): JSX.Element => (
        <div>
          <div className="mb-4">
            <Label name="defaultTransparency">
              Default Transparency
              {props.changedFields?.includes('defaultTransparency') && <Changed />}
            </Label>
            <TextInputLarge {...props.register('defaultTransparency')} placeholder="Default Transparency" />
            <FieldGuide>Set the default transparency percentage of the layer. 0 is fully transparent and 100 is fully visible..</FieldGuide>
          </div>
          <div className="mb-4">
            <Label name="name">
              Feature Scale Limit
              {props.changedFields?.includes('featureScaleLimit') && <Changed />}
            </Label>
            <TextInputLarge {...props.register('featureScaleLimit')} placeholder="Feature Scale Limit" />
          </div>
          <div className="mb-4">
            <Label name="name">
              Max Scale Denominator
              {props.changedFields?.includes('maxScaleDenominator') && <Changed />}
            </Label>
            <TextInputLarge {...props.register('maxScaleDenominator')} placeholder="Max Scale Denominator" />
          </div>
          <div className="mb-4">
            <Label name="name">
              Min Scale Denominator
              {props.changedFields?.includes('minScaleDenominator') && <Changed />}
            </Label>
            <TextInputLarge {...props.register('minScaleDenominator')} placeholder="Min Scale Denominator" />
          </div>
          <div className="mb-4">
            <Label name="name">
              Refresh Interval Seconds
              {props.changedFields?.includes('refreshIntervalSeconds') && <Changed />}
            </Label>
            <TextInputLarge {...props.register('refreshIntervalSeconds')} placeholder="Refresh Interval Seconds" />
          </div>
          <div className="mb-4">
            <Checkbox2 {...props.register('requestGlobalFeatures')} label="Request Global Features" defaultChecked={props.getValues().requestGlobalFeatures} />
          </div>
          <div className="mb-4">
            <Label name="name">
              Legend file path
              {props.changedFields?.includes('url') && <Changed />}
            </Label>
            <TextInputLarge {...props.register('url')} placeholder="Legend file path" />
          </div>
          <div className="mb-4">
            <Label name="name">
              Depth
              {props.changedFields?.includes('depth') && <Changed />}
            </Label>
            <TextInputLarge {...props.register('depth')} placeholder="Depth" />
          </div>
          <Button.LightBlackSmall onClick={(): void => setMapVisibility(true)}>Show Map</Button.LightBlackSmall>
        </div>
      )}
    </MapLayerEditorContainer>
    {showMap && (
      <Dialog onCancel={onCancel} className="map-dialog" backdropClose={false}>
        <DialogTitle>
          <span>Drawing info</span>
          {showDraw ? (
            <span className="draw-btn" onClick={(): void => setShowDraw(false)}>
              <Icons.Pen />
              Cancel Drawing
            </span>
          ) : (
            <span className="draw-btn" onClick={(): void => setShowDraw(true)}>
              <Icons.Pen />
              Start Drawing
            </span>
          )}
        </DialogTitle>
        <DialogContent>
          <MapWrapper bounds={getBounds()} showDraw={showDraw} onChange={onChange} />
        </DialogContent>
        <DialogActions>
          <Button.Tertiary onClick={onCancel}>Cancel</Button.Tertiary>
          <Button.Primary onClick={onSave}>Save</Button.Primary>
        </DialogActions>
      </Dialog>
    )}
  </>;
};
